import { FC, ReactNode, createContext, useContext, useMemo } from 'react';
import { getClientName } from '../helpers/clientHelpers';
import { useQuery } from '@apollo/client';
import { GET_CLIENT_BY_NAME } from '../GraphQL/graphQueries/clientQueries';
import Loading from '../components/Loading';
import { IGetClientByNameResponse } from '../GraphQL/responseTypes';
import StyledWrapper from '../components/StyledWrapper';
import { IClientStyling } from '../types';
import { DEFAULT_COLORS } from '../constants';
import _ from 'lodash';

export interface DynamicStylingContext {
  clientStyling: IClientStyling;
  clientName: string;
}

const FALLBACK_STYLING: IClientStyling = {
  colors: DEFAULT_COLORS,
  thumbnail: '/logo.png',
  logo: '/regWriterLarge.png',
  landingTitle: 'RegWriter',
};

const normalizeAssetUrl = (url: string) => {
  const trimmedUrl = (url || '').trim();
  if (!trimmedUrl) return trimmedUrl;

  // Keep fully-qualified and special protocol URLs unchanged.
  if (/^(https?:|data:|blob:|\/\/)/i.test(trimmedUrl)) {
    return trimmedUrl;
  }

  // Convert route-relative paths to app-root absolute paths.
  if (trimmedUrl.startsWith('./')) {
    return `/${trimmedUrl.slice(2)}`;
  }

  if (trimmedUrl.startsWith('/')) {
    return trimmedUrl;
  }

  return `/${trimmedUrl}`;
};

export const DynamicStylingContext = createContext<DynamicStylingContext | undefined>(undefined);

export const DynamicStylingContextProvider: FC = ({ children }: { children?: ReactNode }) => {
  const clientName = useMemo(() => getClientName(), []);

  const { data, loading, error } = useQuery<IGetClientByNameResponse>(GET_CLIENT_BY_NAME, {
    variables: { name: clientName },
  });

  if (loading && !error) return <Loading type="spin" color="#000" height={100} width={100} />;

  // Fallback styling, just in case the client doesn't have any styling set up
  const clientStyling = data?.getClientByName
    ? (() => {
        const logo = data.getClientByName.logo || FALLBACK_STYLING.logo;
        const thumbnail = data.getClientByName.thumbnail || logo || FALLBACK_STYLING.thumbnail;

        return {
          thumbnail: normalizeAssetUrl(thumbnail),
          logo: normalizeAssetUrl(logo),
          landingTitle: data.getClientByName.landingTitle || FALLBACK_STYLING.landingTitle,
          colors: _.merge(FALLBACK_STYLING.colors, data.getClientByName.styling.colors),
        };
      })()
    : {
        ...FALLBACK_STYLING,
        thumbnail: normalizeAssetUrl(FALLBACK_STYLING.thumbnail),
        logo: normalizeAssetUrl(FALLBACK_STYLING.logo),
      };

  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    (link as HTMLLinkElement).rel = 'icon';
    document.getElementsByTagName('head')[0].appendChild(link);
  }
  (link as HTMLLinkElement).href = '/logo.png';
  document.title = clientStyling?.landingTitle;

  return (
    <DynamicStylingContext.Provider value={{ clientStyling, clientName }}>
      <StyledWrapper id="styled-wrapper" clientStyling={clientStyling}>
        {children}
      </StyledWrapper>
    </DynamicStylingContext.Provider>
  );
};

export const useDynamicStylingContext = (): DynamicStylingContext => {
  const context = useContext(DynamicStylingContext);

  if (context === undefined) {
    throw new Error('useDynamicStylingContext must be used within a DynamicStylingContextProvider');
  }

  return context;
};
